// @vendors
import React, { useContext } from 'react'
import { isEmpty as _isEmpty } from "lodash";
import sx from "mui-sx";
import { useDispatch, useSelector } from "react-redux";
import { TableSortLabel } from "@mui/material";
import { visuallyHidden } from '@mui/utils';

// @materialui
import {
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    Box
} from "../../../components/shared/MaterialUI.js";

// @assets
import { EmployeeTableStyles } from '../../../assets/css/employeeTable-style.js';

// @components
import { SearchEmployeeContext } from '../SearchEmployees.js';
import EmployeeRow from '../../Employer/components/EmployeeTable/components/EmployeeRow.js';
import TableToolbarSearch from '../components/TableToolbarSearch.js';
import { TablePaginationActions } from '../../Admin/components/custom/TablePaginationActions.js';

// @constants
import { types } from "../../../redux/types/index.js";

const SearchEmployeeTable = () => {
  const styles = EmployeeTableStyles();
  const {
    tableState,
    setTableState,
    fetchAllEmployees
  } = useContext(SearchEmployeeContext);
  const dispatch = useDispatch()
  const sortEmployee = useSelector((loadedState) => loadedState.sortEmployeeReducer);

  const handlePageChange = (event, nextPage) => {
    const page = nextPage === 0 ? 1 : nextPage === tableState.currentPage ? nextPage + 1 : nextPage
    setTableState((current) => ({
      ...current,
      currentPage: parseInt(page),
      pageSize: tableState.pageSize
    }));
    fetchAllEmployees({page:page, size:parseInt(tableState.pageSize)})
  };

  const handlePageSizeChange = (event) => {
    setTableState((current) => ({
      ...current,
      currentPage: 1,
      pageSize: event.target.value
    }));
    fetchAllEmployees({size:event.target.value})
  };

  const createSortHandler = (property) => (event) => {
    onRequestSort(event, property);
  };

  const onRequestSort = (event, property) => {
    const isAsc = sortEmployee.sort === property && sortEmployee.direction === 'asc';
    sortDispatch(property, isAsc ? 'desc' : 'asc')
    fetchAllEmployees({sort:property, direction:isAsc ? 'desc' : 'asc'})
  };
  
  const onRequestSortCertification = (property, option) => {
    sortDispatch(property, option)
    fetchAllEmployees({sort:property, direction:option})
  };

  const sortDispatch = (property, option) => {
    return dispatch({
        type: types.SORT_EMPLOYEE,
        sort: property,
        direction: option
      })
  }

  const sortTableCell = (label, nameToSearch) => {
    return (
      <TableSortLabel
        active={sortEmployee.sort === nameToSearch ? true : false}
        direction={sortEmployee.sort === nameToSearch ? sortEmployee.direction : 'asc'}
        onClick={createSortHandler(nameToSearch)}
        sx={{ ...styles.tableHeadTitle}}
      >
        {label}
          <Box component="div" sx={visuallyHidden}>
            {sortEmployee.direction === 'desc' ? 'sorted descending' : 'sorted ascending'}
          </Box>
      </TableSortLabel>
    )
  }

  return (
      <Paper elevation={4}>
        <TableToolbarSearch/>
        <TableContainer sx={styles.tableContainer}>
          <Table aria-label="collapsible table" stickyHeader>
            <TableHead>
              <TableRow sx={styles.tableRow}>
                <TableCell sx={styles.emptyTableCell} />

                <TableCell
                  sx={{ ...styles.tableHeadTitle, textAlign: 'left' }}
                  sortDirection={sortEmployee.sort === 'company' ? sortEmployee.direction : false}
                >
                  {sortTableCell('Company', 'company')}
                </TableCell>

                <TableCell
                  sx={{ ...styles.tableHeadTitle, textAlign: 'left' }}
                  sortDirection={sortEmployee.sort === 'name' ? sortEmployee.direction : false}
                >
                  {sortTableCell('Full name', 'name')}
                </TableCell>

                <TableCell 
                  sx={{ ...styles.tableHeadTitle, textAlign: 'left' }}
                  sortDirection={sortEmployee.sort === 'update' ? sortEmployee.direction : false}
                >
                  {sortTableCell('Last Modified Date', 'update')}
                </TableCell>

                <TableCell 
                  sx={sx(styles.tableHeadTitle, styles.tableColumnHide)}
                  sortDirection={sortEmployee.sort === 'status' ? sortEmployee.direction : false}
                >
                  {sortTableCell('Questionnaire Status', 'status')}
                </TableCell>

                <TableCell 
                  sx={sx(styles.tableHeadTitle, styles.tableColumnHide)}
                  sortDirection={sortEmployee.sort === 'days' ? sortEmployee.direction : false}
                >
                  {sortTableCell('Remaining Days', 'days')}
                </TableCell>

                <TableCell 
                  sx={sx(styles.tableHeadTitle, styles.tableColumnHide)}
                  sortDirection={sortEmployee.sort === 'groups' ? sortEmployee.direction : false}
                >
                  {sortTableCell('Target Group', 'groups')}
                </TableCell>

                <TableCell
                  sx={sx(styles.tableHeadTitle, styles.tableColumnHide)}
                  sortDirection={sortEmployee.sort === 'nsi' ? sortEmployee.direction : false}
                >
                  {sortTableCell('NSI', 'nsi')}
                </TableCell>

                <TableCell 
                  sx={sx(styles.tableHeadTitle, styles.tableColumnHide)}
                  sortDirection={sortEmployee.sort === 'wotc' ? sortEmployee.direction : false}
                >
                  {sortTableCell('WOTC eligibility', 'wotc')}
                </TableCell>

                <TableCell 
                  sx={sx(styles.tableHeadTitle, styles.tableColumnHide)}
                  sortDirection={sortEmployee.sort === 'submit' ? sortEmployee.direction : false}
                >
                  {sortTableCell('Ready to Submit', 'submit')}
                </TableCell>

                <TableCell
                  sx={sx(
                    styles.tableHeadTitle,
                    styles.tableHeadMax,
                    styles.tableColumnHide,
                    {
                      condition: _isEmpty(tableState.rows),
                      sx: styles.loadCell,
                    }
                  )}
                >
                  State certification
                  {_isEmpty(tableState.rows) ? null :
                    <div style={{display: 'flex', gap: 0, marginTop: '2px'}}>
                      <div>
                        <TableSortLabel
                          active={sortEmployee.sort === 'certification' && sortEmployee.direction === 'Yes' ? true : false}
                          direction={'asc'}
                          onClick={() => onRequestSortCertification('certification', 'Yes')}
                          sx={{ ...styles.tableHeadTitle}}
                        >
                           Yes
                        </TableSortLabel>
                      </div>
                      <div>
                        <TableSortLabel
                          active={sortEmployee.sort === 'certification' && sortEmployee.direction === 'No' ? true : false}
                          direction={'asc'}
                          onClick={() => onRequestSortCertification('certification', 'No')}
                          sx={{ ...styles.tableHeadTitle}}
                        >
                          No
                        </TableSortLabel>
                      </div>
                      <div>
                        <TableSortLabel
                          active={sortEmployee.sort === 'certification' && sortEmployee.direction === 'Pending' ? true : false}
                          direction={'asc'}
                          onClick={() => onRequestSortCertification('certification', 'Pending')}
                          sx={{ ...styles.tableHeadTitle}}
                        >
                          Pending
                        </TableSortLabel>
                      </div>
                    </div>
                  }
                </TableCell>
                              
                <TableCell 
                  sx={sx(
                    styles.tableHeadTitle,
                    styles.tableHeadMax,
                    styles.tableColumnHide,
                    {
                      condition: _isEmpty(tableState.rows),
                      sx: styles.loadCell,
                    }
                  )}
                  sortDirection={sortEmployee.sort === 'ez' ? sortEmployee.direction : false}
                >
                  {sortTableCell('EZ', 'ez')}
                </TableCell>
                
                <TableCell 
                  sx={sx(
                    styles.tableHeadTitle,
                    styles.tableHeadMax,
                    styles.tableColumnHide,
                    {
                      condition: _isEmpty(tableState.rows),
                      sx: styles.loadCell,
                    }
                  )}
                  sortDirection={sortEmployee.sort === 'rrc' ? sortEmployee.direction : false}
                >
                  {sortTableCell('RRC', 'rrc')}
                </TableCell>

                <TableCell 
                  sx={sx(styles.tableHeadTitle, styles.tableColumnHide)}
                  sortDirection={sortEmployee.sort === 'sy' ? sortEmployee.direction : false}
                >
                  {sortTableCell('Summer Youth', 'sy')}
                </TableCell>
                
                <TableCell sx={styles.emptyTableCell} />
              </TableRow>
            </TableHead>
            <TableBody>
              {React.Children.toArray(
                tableState?.rows &&
                tableState?.rows.map((row) => <EmployeeRow
                        row={row}
                        isSearch={true}
                        getAllEmployesByCompany={fetchAllEmployees}
                        search={tableState.filters}
                        pages={tableState}
                    />
                )
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          rowsPerPageOptions={[10, 20, 50, 100]}
          colSpan={3}
          count={tableState.rowCount || 0}
          rowsPerPage={parseInt(tableState.pageSize)}
          page={tableState.page}
          onPageChange={handlePageChange}
          onRowsPerPageChange={(e) => handlePageSizeChange(e)}
          ActionsComponent={() => TablePaginationActions(tableState.rowCount, tableState.currentPage, tableState.pageSize, handlePageChange)}
          SelectProps={{
            inputProps: {
              "aria-label": "rows per page",
            },
            native: true,
          }}
        />
      </Paper>
  );
}

export default SearchEmployeeTable